// Import utilities for the api.
import { h, l, g, Cookie, LastSeen, Request, Visits, Device } from './Utils';
import {
	clearOmCookiesStorage,
	createVisitorId,
	currentTime,
	getDomain,
	getQueryArg,
	getHeader,
	getUrl,
	keys,
	param,
	trigger,
	urlPathFull,
} from './Utils/Helpers';

const getRevenue = () => import(/* webpackChunkName: "Revenue" */ './Utils/Revenue');
const getCampaignsModule = () => import(/* webpackChunkName: "Campaigns" */ './Campaigns');

/**
 * The main App class.
 *
 * @since 5.0.0
 *
 * @returns {void}
 */
export default class Main {
	/**
	 * "Class" constructor in ES6.
	 *
	 * @since 5.0.0
	 *
	 * @returns {void}
	 */
	constructor() {
		// Set some default variables for the class.
		this.embed = null;
		this.defaults = {};
		this.campaigns = null;
		this.list = null;
		this.urlPath = null;
	}

	/**
	 * A default method to initialize the class.
	 *
	 * @since 5.0.0
	 *
	 * @param   {Object} data The data from the old embed code or built in Api.js.
	 * @returns {void}
	 */
	init(data) {
		window[g.GLOBAL_OM]._utils = {
			events: {
				trigger: trigger,
			},
			helpers: h,
			strings: g.STRINGS,
			Cookie: Cookie,
			Request: Request,
			Device: Device,
		};

		trigger(document, 'Main.init', {
			Main: this,
			_utils: window[g.GLOBAL_OM]._utils,
		});

		// Expose a reference for device-fallback flows
		window[g.GLOBAL_OM].mainInstance = this;

		// Maybe set or delete local storage keys.
		this.localStorage();

		// Let's set the cookies for the user.
		this.cookies();

		// We need to set up some defaults before we try to grab the campaigns.
		this.setDefaults(data);

		// If the `om-verify-site=true` query arg is present, bail out before
		// anything else happens. We don't want to track or load campaigns.
		if (data.verify) {
			return;
		}

		// Let's track visits.
		new Visits();

		// Initialize revenue attribution tracking.
		this.revenueAttribution();

		// Determine device by viewport before requesting
		Device.parseDeviceInfo(true);

		// Debug device detection
		try {
			const viewportWidth =
				(window && window.innerWidth) ||
				(document && document.documentElement && document.documentElement.clientWidth) ||
				1025;
			console.log('Detected device via viewport', {
				device: Device.get(),
				viewportWidth,
			});
		} catch (e) {
			// no-op
		}

		// Let's get our campaigns.
		this.getCampaigns();
	}

	/**
	 * Set up any local storage, or delete keys if the `omclear` parameter is set.
	 *
	 * @since 5.13.x
	 *
	 * @returns {void}
	 */
	localStorage = () => {
		if (clearOmCookiesStorage()) {
			LastSeen.delete();
		}
	};

	/**
	 * Sets unique cookies for our visitors, or deletes them if the omclear parameter is set.
	 *
	 * @since 5.0.0
	 *
	 * @returns {void}
	 */
	cookies = () => {
		// We'll set two cookies here, persistent and session visitor.
		// If persistent visitor already exists, we just extend the time.
		// This is how we determine new vs. returning visitors.

		// Ok, if someone wants to clear cookies, we will do that now.
		if (clearOmCookiesStorage()) {
			Cookie.deleteOmCookies();
		}

		let cookieVal = Cookie.getPersistent();
		const newSession = !cookieVal;

		// Check if the persistent cookie value is missing or is "true".
		// If so, set a custom visitor ID.
		if (newSession || 'true' === cookieVal) {
			cookieVal = createVisitorId();
		}

		Cookie.setPersistent(cookieVal);

		// If we just set a persistent visitor cookie, we need to create the
		// session cookie as well. Otherwise if we have a session cookie still
		// intact, let's extend it for the current user.
		if (newSession || Cookie.getSession()) {
			Cookie.setSession();
		}
	};

	/**
	 * Set up some defaults that will persist throughout the app.
	 *
	 * @since 5.0.0
	 *
	 * @param   {Object} data The data to parse.
	 * @returns {void}
	 */
	setDefaults = (data) => {
		// If an embed code type has not been set, lets do that now.
		if (!this.embed) {
			this.embed = data.a || data.s ? 'account' : 'campaign';
			if (data.s) {
				data.a = data.s.split('.')[0];
				data.u = data.a;
			}
		}

		// If we are missing a user from the old embed code, lets make that now.
		if ('undefined' === typeof data.user) {
			let start = 'undefined' !== typeof data.a ? data.a : 'undefined' !== typeof data.s ? data.s : data.u;
			data.user = start.toString().split('.')[0];

			// Since no user was set, we know this is the old embed code.
			data.oldEmbed = true;

			// The old embed code does not have the page start time which we need for the SiteTime rule.
			data.pageStart = currentTime();

			// Let's set up the environment here as well.
			data.env = data.dev ? 'dev' : data.staging ? 'staging' : data.beta ? 'beta' : 'production';
		}

		data.envApi =
			'production' === data.env
				? 'api'
				: 'beta' === data.env
					? 'beta-api'
					: 'staging' === data.env
						? 'staging-api'
						: 'api';
		data.envMin = 'production' === data.env ? '.min' : '';

		// By default all of these are false, but they can be manually changed later.
		data.preview = false;
		data.demo = false;
		data.public = data.public || false;
		data.override = getQueryArg('om-live-preview') || false;
		data.verify = getQueryArg('om-verify-site') || false;

		if (data.override) {
			data.campaign = data.override;
		}

		// Set the defaults globally so we can access them from anywhere in the app.
		this.defaults = data;

		// Make the request to verify the site.
		if (data.verify && !window._omsiteverified) {
			// Each embed will make it's own verify request. Setting this
			// global will limit it to one (maybe two depending on race
			// conditions) request to the verify route.
			window._omsiteverified = true;
			import(/* webpackChunkName: "Verifier" */ './Verifier').then((Verifier) => {
				const verifier = new Verifier.default(this.defaults);
				verifier.verifySite();
			});
		}
	};

	/**
	 * Retrieve campaigns.
	 *
	 * @since 5.9.21
	 *
	 * @param   {Object} data The data to parse.
	 * @returns {void}
	 */
	getCampaigns = () => {
		trigger(document, 'Main.getCampaigns', {
			Main: this,
		});

		const device = Device.get();
		console.log('Requesting campaigns', {
			device,
			env: this.defaults?.env,
			envApi: this.defaults?.envApi,
		});
		this.requestCampaignsForDevice(device, 1)
			.then((xhr) => this.parse(xhr))
			.catch((xhr) => this.errors(xhr));
	};

	/**
	 * Build and send the embed request for a specific device, with retry support.
	 *
	 * @param {string} device The viewport-selected device key
	 * @param {number} retries Number of additional attempts before falling back to desktop
	 * @returns {Promise<XMLHttpRequest>} Resolves with an XHR-like object containing `response`
	 */
	requestCampaignsForDevice = (device, retries = 0) => {
		// Determine endpoint and query params
		let endpoint = '';
		let useList = false;
		let urlPath = urlPathFull();
		let qs = {};

		const rawData = window?.app?.embedData;
		const isTestingContext =
			this.defaults.override || (this.defaults.ruleTest && this.defaults.ruleTest === this.defaults.campaign);

		console.log('Building embed request', {
			requestedDevice: device,
			retries,
			flags: {
				hasA: !!this.defaults.a,
				hasU: !!this.defaults.u,
				override: !!this.defaults.override,
				oldEmbed: !!this.defaults.oldEmbed,
				preview: !!this.defaults.preview,
			},
		});

		if (this.defaults.a && !this.defaults.override) {
			endpoint = this.defaults.a;
			if (this.validList()) {
				useList = true;
			}
			qs.d = getDomain();
		} else if (this.defaults.u && rawData && !isTestingContext) {
			// If embedData already present (builder context), bypass network
			console.log('Using builder embedData; bypassing network request', {
				requestedDevice: device,
				isTestingContext,
			});
			return Promise.resolve({ response: JSON.stringify(rawData) });
		} else if (this.defaults.u || isTestingContext) {
			endpoint = this.defaults.oldEmbed
				? this.defaults.u.replace('.', '/')
				: this.defaults.user + '/' + this.defaults.campaign;
			if (this.defaults.preview || isTestingContext) {
				endpoint += '/preview';
			}
		}

		qs.device = device;

		let request = new Request();
		let apiUrl = getUrl(this.defaults, 'embed', endpoint);

		if (this.defaults.override || this.defaults.preview || useList) {
			request.setCache(false);
		}

		if (keys(qs).length) {
			apiUrl += '?' + param(qs);
		}

		request.get(apiUrl);

		if (urlPath && (getQueryArg('omSendUrlPath') || this.urlPath)) {
			request.setHeader('X-OptinMonster-UrlPath', urlPath);
		}

		if (useList) {
			request.setHeader('X-OptinMonster-Campaigns', this.list.join());
		}

		console.log('Embed request prepared', {
			url: apiUrl,
			qs,
			headers: request.headers,
		});

		return request
			.send()
			.then((xhr) => {
				let serverDevice = '';
				try {
					serverDevice =
						getHeader(xhr, 'X-OptinMonster-Device') || getHeader(xhr, 'x-optinmonster-device') || '';
				} catch (e) {}
				console.log('Embed response received', {
					requestedDevice: device,
					status: xhr?.status,
					responseURL: xhr?.responseURL,
					serverDevice,
				});
				return xhr;
			})
			.catch((err) => {
				console.log('warn', 'Embed request failed', {
					requestedDevice: device,
					status: err?.status,
					retriesRemaining: retries,
				});
				if (0 < retries) {
					console.log('Retrying same device', { device, nextRetries: retries - 1 });
					return this.requestCampaignsForDevice(device, retries - 1);
				}
				if ('desktop' !== device) {
					console.log('Falling back to desktop device');
					return this.requestCampaignsForDevice('desktop', 1);
				}
				return Promise.reject(err);
			});
	};

	/**
	 * When the campaigns have been successfully retrieved, lets parse them here.
	 *
	 * @since 5.0.0
	 *
	 * @param   {XMLHttpRequest} xhr The entire xhr object so we can grab headers, etc.
	 * @returns {void}
	 */
	parse = (xhr) => {
		trigger(document, 'Main.parseCampaigns', {
			Main: this,
			xhr: xhr,
		});

		// Device was already parsed before request; keep reference
		const device = Device.get();
		console.log('Parsing campaigns response', {
			requestedDevice: device,
			responseURL: xhr?.responseURL,
			length: xhr?.response ? xhr.response.length : 0,
		});

		// Set up some default variables.
		let campaigns = JSON.parse(xhr.response);

		// If server returned device-scoped payload, normalize to expected views
		const normalize = (c) => {
			if (c && c.devices && !c.views) {
				const keysDevices = Object.keys(c.devices || {});
				const dKey = keysDevices.includes(device) ? device : keysDevices[0];
				if (keysDevices.length && dKey !== device) {
					console.log('Device key not available; normalizing to first available device', {
						requestedDevice: device,
						availableDevices: keysDevices,
						chosen: dKey,
						id: c?.id || c?.slug || c?.name,
					});
				}
				const views = c.devices[dKey] || [];
				c.views = Array.isArray(views) ? views : [];
			}
			return c;
		};

		if (Array.isArray(campaigns)) {
			campaigns = campaigns.map((c) => normalize(c));
		} else if (campaigns && campaigns.campaigns) {
			campaigns.campaigns = campaigns.campaigns.map((group) => group.map((c) => normalize(c)));
		} else if (campaigns && campaigns.campaign) {
			campaigns.campaign = normalize(campaigns.campaign);
		}

		// Now we can check for new campaigns.
		if (campaigns.campaigns || Array.isArray(campaigns)) {
			this.instantiateCampaigns(campaigns);
		}
	};

	/**
	 * Instantiates the Campaigns object from the embed data.
	 *
	 * @since 5.0.0
	 *
	 * @param   {Object} campaigns The campaigns embed data object.
	 *
	 * @returns {void}
	 */
	instantiateCampaigns = (campaigns) => {
		trigger(document, 'Main.instantiateCampaigns', {
			Main: this,
		});

		getCampaignsModule().then(
			(Campaigns) => (this.campaigns = new Campaigns.default(campaigns, this.embed, this.defaults))
		);

		window[g.GLOBAL_OM].device = Device.get(); // Add to our global for easy reference.
	};

	/**
	 * Any errors that come back from the request will show up here.
	 *
	 * @since 5.0.0
	 *
	 * @param   {Object|string} error The error can either be an object or a string.
	 * @returns {void}
	 */
	errors = (error) => {
		trigger(document, 'Main.getCampaigns.error', {
			Main: this,
			error,
		});

		// Parse the response if it exists.
		const response = error.response ? JSON.parse(error.response) : null;

		// Don't send errors to the console when we're in maintenance mode.
		// Some customers run error logging software on their sites, and this
		// has the potential to spam user's error logging service. Here, we warn
		// rather than error when we are in maintenance mode.
		if (503 === error.status && true === response?.maintenance) {
			l.warn('The OptinMonster API is temporarily unavailable.');
			return;
		}

		// Display the error.
		if (response) {
			l.error(response?.message ?? response?.error ?? '');
		} else {
			l.error(error);
		}
	};

	/**
	 * A way to reset each campaign attached to the main window object.
	 *
	 * @since 5.0.0
	 *
	 * @returns {void}
	 */
	reset = () => {
		if (this.campaigns) {
			this.campaigns.reset();
		}
	};

	/**
	 * Validate the list given to make sure it is in the proper structure.
	 *
	 * @since 5.9.25
	 *
	 * @returns {boolean} Whether or not the list is valid.
	 */
	validList = () => {
		// If it's null, return without any error message.
		if (null === this.list) {
			return false;
		}

		// If it's not an array, return false.
		if (!Array.isArray(this.list)) {
			console.log('The campaign list override is not in the proper format.');
			return false;
		}

		// Filter out any elements that are not a string.
		this.list = this.list.filter((i) => {
			return 'string' === typeof i;
		});

		// If there is no content in the array, return false.
		if (!this.list.length) {
			console.log('The campaign list override is not in the proper format.');
			return false;
		}

		// All validation checks out, return true.
		return true;
	};

	/**
	 * Fire the event for revenue attribution tracking.
	 *
	 * @returns {void}
	 */
	revenueAttribution = () => {
		// Tracking function.
		const track = (type, args) => {
			getRevenue().then((Revenue) => new Revenue.default(this, type, args));
		};

		const trackArray = (params) => track(...params);

		// Check for already generated attributions
		if (window._omq && Array.isArray(window._omq)) {
			window._omq.forEach(trackArray);
		}

		window._omq = [];

		// This is for the new omq rev-attribution snippet.
		window._omq.push = trackArray;

		// Set global function for revenue attribution tracking.
		// This is for back-compat with previous usage.
		window.omq = window.omq || track;

		trigger(document, 'RevenueAttribution.init', {
			track,
			Main: this,
			_utils: window[g.GLOBAL_OM]._utils,
		});
	};
}
